/**
 * meter/math — the meter paint path's shared scalar shaping.
 *
 * Scalar interpolation (`lerp`) is value.js's `math` authority — this
 * module does NOT redeclare it. AV.W8 hoisted one byte-identical `lerp`
 * out of `paintMeter.ts` + `meterCompletion.ts` to a single source; AV.W11b
 * (Band-C suffusion) terminal-homes that source onto value.js `lerp`, so the
 * five paint call sites now import `lerp` directly from `@mkbabb/value.js`.
 * NOTE the argument order flips at the boundary: the retired local hoist took
 * `(t, start, end)` (value first); value.js `lerp` takes `(start, end, t)`
 * (range first) — every call site was reordered, not merely re-pointed.
 *
 * What lives HERE is the meter's OWN domain shape — the two-segment phase-
 * handoff remap the needle (`useMeterRenderer`) and the under-meter bar
 * (`MeterColumn.vue`) both play. It is NOT scalar interpolation and is NOT a
 * library primitive (no keyframes/value.js export shapes a piecewise
 * retreat-then-advance over an externally-driven clock); it is the meter's
 * choreography, so it is colocated with the meter composables. See
 * docs/decisions/DDR-AV-W11B-SUFFUSION.md (Timing lane).
 */

/**
 * Fraction of the phase-handoff clock the leading channel spends RETREATING
 * (the needle eases its fill back toward 0, the bar DRAINS) before either
 * advances toward the new phase's target — the first beat of the three-beat
 * handoff shape (retreat → cross-fade → arrive). The single declared value of
 * the magic `1/3` that previously lived twice (the renderer's `RETREAT`, the
 * bar's local `DRAIN`); both now read it from here so the two channels can
 * never drift their split point. Aligned with the `PHASE_HANDOFF_MS` /
 * `MILESTONE_BLOOM_MS` motion-constant family.
 */
export const HANDOFF_RETREAT_FRACTION = 1 / 3;

/**
 * The shared two-segment phase-handoff shape. Given the eased handoff clock
 * `p` (0→1, driven by the unified `usePhaseTransition` `ManualTimeline`), the
 * `origin` fill captured at the boundary, and the new phase's live `target`:
 *
 * - for `p < split` the channel RETREATS `origin → 0` (the instrument
 *   "rewinds to measure again"); linear in the already-eased clock.
 * - for `p >= split` the channel ADVANCES `0 → target` over the remaining
 *   clock; also linear in the eased clock.
 *
 * `easeInOutCubic` lives in the ManualTimeline upstream, so each leg is a
 * plain linear remap of the eased `p` — the curve is not re-applied here.
 * Pure scalar, zero allocation: it runs once per channel per handoff frame on
 * the rAF path, so it stays a bare arithmetic shape (no `NumericAnimation`
 * per-frame construction — that would allocate a timeline every frame the
 * boundary is live; see the DDR's perf note).
 *
 * The needle passes its `targetT` raw (already 0..1 from `metricProgress`);
 * the bar passes a caller-clamped live progress. Callers that need 0..1
 * bounding clamp at THEIR boundary (the bar does), so this stays the bare
 * shape both channels share.
 */
export function handoffRemap(
    p: number,
    origin: number,
    target: number,
    split: number = HANDOFF_RETREAT_FRACTION,
): number {
    return p < split
        ? origin * (1 - p / split)
        : target * ((p - split) / (1 - split));
}
